import { devWarnMissingAccessibleNameOnce } from '@/lib/a11y/accessibilityWarnings'
import { twMerge } from '@/lib/classname/twMerge'
import { BaseSkeleton } from '@/ui/skeleton/BaseSkeleton'
import { type VariantProps, cva } from 'class-variance-authority'

export const statusDotVariantKeys = [
  'success',
  'warning',
  'error',
  'info',
  'unavailable',
  'default',
] as const

export type StatusDotVariant = (typeof statusDotVariantKeys)[number]

export const statusDotVariants = {
  success: 'text-success-fg bg-current',
  warning: 'text-warning-fg bg-current',
  error: 'text-error-fg bg-current',
  info: 'text-info-fg bg-current',
  unavailable: 'text-error-fg border-current border-px hd:border-1.5 border-solid',
  default: 'text-brand-2-900 bg-current',
} satisfies Record<StatusDotVariant, string>

const statusDotCva = cva('block size-[0.625rem] rounded-full', {
  variants: {
    variant: statusDotVariants,
  },
  defaultVariants: {
    variant: 'success',
  },
})

export type StatusDotVariantProps = VariantProps<typeof statusDotCva>

export type StatusDotProps = WithTestId<
  StrictOmit<React.ComponentPropsWithoutRef<'span'>, 'children' | 'className' | 'id'> & {
    /** The variant style/status */
    variant: StatusDotVariant
    /** Optional id applied to the root element */
    id?: string
    className?: string
  }
>

export function StatusDot({
  variant,
  className,
  'data-testid': dataTestId,
  'aria-hidden': ariaHidden = true,
  ...props
}: StatusDotProps) {
  devWarnMissingAccessibleNameOnce({
    componentName: 'StatusDot',
    role: props.role,
    ariaHidden,
    ariaLabel: props['aria-label'],
    ariaLabelledBy: props['aria-labelledby'],
  })

  return (
    <span
      aria-hidden={ariaHidden}
      {...props}
      className={twMerge(className, statusDotCva({ variant }))}
      data-testid={dataTestId ?? 'status-dot'}
    />
  )
}

export function StatusDotSkeleton({ className }: { className?: string }): React.ReactElement {
  return (
    <BaseSkeleton className={twMerge('size-[0.625rem] shrink-0 grow-0 rounded-full', className)} />
  )
}
